import { useEffect, useState } from 'react';
import { IconButton, IconCaretCircleLeft, IconCaretCircleRight } from '@lmig/lmds-react-icons';
import { Link } from '@lmig/lmds-react-link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './RecentActivities.scss';
import { faSortDown } from '@fortawesome/free-solid-svg-icons';
import { useUserData } from 'Foundation/User/UserContext';
import { CircularProgressSpinner } from '@lmig/sales-ui-circular-progress-spinner';
import { getBillingURLByPolicy, getClaimURL, getPolicyURLByHref } from 'Utilities/URLHelper';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@lmig/lmds-react-table';
import BillingAccountSelect, { IBillingAccount } from './BillingAccountSelect';

export default function TableColumnHeader(props: {
  link: ISitecoreLink | string;
  columns: any[];
  sortedColumn: any;
  activityDataType: string;
  sortingFn: (arg0: any, arg1: string, arg2: any[] | null, arg3: boolean) => void;
  sortedItems: any[];
}) {
  const [sending, setSending] = useState(false);
  const [billingModalIsOpen, setBillingModalIsOpen] = useState<boolean>();
  const [billingAccounts, setBillingAccounts] = useState<IBillingAccount[]>();
  const { userData } = useUserData();
  const [currentPage, setCurrentPage] = useState(1);
  const [nearByPages, setNearbyPages] = useState([1]);
  const resultsPerPage = 10;

  function buildPolicyLink(item: any, columnsItem: any) {
    if (!userData) return;

    if (item.PolicyHref) {
      return (
        <Link variant={columnsItem.columnVariant} href={getPolicyURLByHref(item.PolicyHref, userData)} target="blank">
          {item[columnsItem.columnKey]}
        </Link>
      );
    }
    if (props.link && props.link !== 'null' && item.ClaimNumber) {
      //redirect to claim inquiry in sitecore if there is a page set on the datasource
      return (
        <Link
          variant={columnsItem.columnVariant}
          href={`${(props.link as ISitecoreLink).Url}?claimNumber=${item.ClaimNumber}`}
        >
          {item[columnsItem.columnKey]}
        </Link>
      );
    } else if (item.ClaimNumber) {
      //open up old claim inquiry
      return (
        <Link
          variant={columnsItem.columnVariant}
          target="_blank"
          href={getClaimURL(item.ClaimNumber, userData, item.AgencyCode)}
        >
          {item[columnsItem.columnKey]}
        </Link>
      );
    } else if (isCommercialLinesPolicy(item[columnsItem.columnKey])) {
      return (
        <Link
          variant={columnsItem.columnVariant}
          style={sending ? { outline: 'none' } : {}}
          onClick={() => redirectToBilling(item.PolicyNumber, item.AgencyCode)}
        >
          {sending && document?.activeElement?.innerHTML === item.PolicyNumber ? (
            <CircularProgressSpinner size="16" aria-label="Loading billing application..." />
          ) : (
            item[columnsItem.columnKey]
          )}
        </Link>
      );
    } else {
      return <Link variant={columnsItem.columnVariant}>{item[columnsItem.columnKey]}</Link>;
    }
  }

  async function redirectToBilling(policy: string, agencyCode: string) {
    if (!userData || sending) return;
    setSending(true);
    try {
      let data: any[] = await getBillingURLByPolicy(policy, agencyCode, userData);
      setSending(false);
      if (data && data.length > 1) {
        setBillingAccounts(
          (data as any[]).map((x) => ({
            accountNumber: x.Number,
            insuredName: x.Insured.Name,
            address: `${x.Insured.Address.StreetLine1} \n ${x.Insured.Address.City}, ${x.Insured.Address.StateProvCd} ${x.Insured.Address.USZipCode}`,
          })),
        );
        setBillingModalIsOpen(true);
      } else if (data && data.length) global.open(data[0] as string, '_blank')?.focus();
    } catch (e) {
      setSending(false);
    }
  }

  useEffect(() => {
    //Return functions in useEffect execute on unmount process.
    //Exists to handle memory leak messages, cleans state on unmount.
    return () => {
      setSending(false);
    };
  }, []);

  function isCommercialLinesPolicy(policyNum: string) {
    if (
      policyNum.match(/([A-Z]{2}|[A-Z]{3}|[A-Z]{4})[0-9]{7}$/) || //CL_AQS_MECCA
      policyNum.match(/[0-9]{7}$/) || //CL_AQS_MECCA_NOSYM
      policyNum.match(/[A-Z]{3}[0-9]{8}$/) || //CL_PARIS_ECLIQ
      policyNum.match(/[0-9]{8}$/) || //CL_PARIS_ECLIQ_NOSYM
      policyNum.match(/((((K|C|A)([0-9]{2}))|BOP)([0-9]{6})|(WC([0-9A-Z]{15})))$/) || //CL_LNW
      policyNum.match(/(([0-9A-Z]{19})|((C|A)([0-9]{8})))$/) || //CL_LNW_AUDIT
      policyNum.match(
        /(([0-9]{2})(BA|BO|BP|BZ|CC|CD|CE|CG|CH|CI|CL|CO|CT|EE|ER|FA|FB|FC|FF|FG|FI|FM|FO|FQ|FR|FT|GL|PE|SU|WC|WK|XS)([0-9]{7})|([0-9]{2})(BA|BO|BP|BZ|CC|CD|CE|CG|CH|CI|CL|CO|CT|EE|ER|FA|FB|FC|FF|FG|FI|FM|FO|FQ|FR|FT|GL|PE|SU|WC|WK|XS)([0-9]{6})(10|20|30|40|50|60|70|80|90))$/,
      )
    ) {
      //CL_SAFECO
      return true;
    } else {
      return false;
    }
  }

  function getTableHeaders() {
    let headers = [];
    for (let i = 0; i < props.columns.length; i++) {
      let item = props.columns[i];
      let tableCellStyle = props.sortedColumn.column === item.columnKey ? 'sortedColumnHeader' : 'unsortedColumnHeader';
      let iconStyle = props.sortedColumn.icon === faSortDown ? 'sortIconDown' : 'sortIconUp';
      let icon =
        props.sortedColumn.column === item.columnKey ? (
          <FontAwesomeIcon icon={props.sortedColumn.icon} className={iconStyle} />
        ) : (
          <></>
        );
      let tableCell = (
        <TableCell
          type="colHead"
          key={item.columnKey}
          className={tableCellStyle}
          onClick={() => props.sortingFn(item.columnKey, props.activityDataType, null, true)}
        >
          {item.columnDisplayText}
          {icon}
        </TableCell>
      );
      headers.push(tableCell);
    }

    return headers;
  }

  useEffect(() => {
    function setPagination(cp: number = currentPage) {
      let array: number[] = [];
      let lp: number = Math.ceil(props?.sortedItems.length / resultsPerPage);
      if (lp <= 5 || cp < 4) {
        /* 1 2 3 4 5 or 1 2 3 4...10 */
        for (let i = 1; i < Math.min(5, lp); i++) array.push(i);
        array.push(lp);
      } else if (lp - 2 < cp) {
        /* 1...7 8 9 10 */
        array.push(1);
        array.push(lp - 3);
        array.push(lp - 2);
        array.push(lp - 1);
        array.push(lp);
      } else if (cp - 2 > 1) {
        /* 1 ...3 4 5...10 */
        array.push(1);
        array.push(cp - 1);
        array.push(cp);
        array.push(cp + 1);
        array.push(lp);
      }
      setNearbyPages(array);
    }

    setPagination();
  }, [currentPage, props.sortedItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const getPagination = () => {
    return (
      <div className="pagination">
        {Math.ceil(props?.sortedItems?.length / resultsPerPage) > 1 ? (
          <>
            {currentPage === 1 ? null : (
              <IconButton
                onClick={() => {
                  setCurrentPage(currentPage - 1);
                }}
              >
                <IconCaretCircleLeft title="Previous Page" size="32" />
              </IconButton>
            )}
            {nearByPages.map((num, index) => (
              <span key={index}>
                {index !== 0 && nearByPages[index - 1] + 1 !== num ? <span>...</span> : null}
                <button
                  className={num === currentPage ? 'active num' : 'num'}
                  onClick={() => {
                    setCurrentPage(num);
                  }}
                >
                  {num}
                </button>
              </span>
            ))}
            {currentPage === Math.ceil(props?.sortedItems?.length / resultsPerPage) ? null : (
              <IconButton
                onClick={() => {
                  setCurrentPage(currentPage + 1);
                }}
              >
                <IconCaretCircleRight title="Next Page" size="32" />
              </IconButton>
            )}
          </>
        ) : null}
      </div>
    );
  };

  return (
    <>
      {userData && (
        <>
          <Table id={props.activityDataType + '-ra-table'} className="uscm-recent-activities-table" rowStyle="zebra">
            <TableHeader>
              <TableRow>{getTableHeaders()}</TableRow>
            </TableHeader>
            <TableBody>
              {props.sortedItems
                .slice((currentPage - 1) * resultsPerPage, currentPage * resultsPerPage)
                .map((item: any) => (
                  <TableRow key={item.Id}>
                    {props.columns.map((columnsItem: any) => (
                      <TableCell key={columnsItem.columnKey}>
                        {columnsItem.columnVariant === null ? (
                          item[columnsItem.columnKey]
                        ) : columnsItem.columnVariant === 'navigational' ? (
                          buildPolicyLink(item, columnsItem)
                        ) : (
                          <Link variant={columnsItem.columnVariant}>{item[columnsItem.columnKey]}</Link>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          {billingAccounts && (
            <BillingAccountSelect
              accounts={billingAccounts}
              isOpen={billingModalIsOpen}
              onClose={() => setBillingModalIsOpen(false)}
            />
          )}
          {getPagination()}
        </>
      )}
    </>
  );
}
